import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DrawerHeader } from '@components/drawer-header';
import { GlobalContext } from 'context/userContext';
import { formatAuditTimestamp } from '@utils/audit-history';
import { summarizeMaintenanceTicket } from '@utils/machine-maintenance';
import {
  MachineMaintenanceTicket,
  closeMachineMaintenance,
  listMachineMaintenances,
  openMachineMaintenance,
  listEquipaments,
} from './api';

const MAINTENANCE_TYPES = [
  { value: 'preventiva', label: 'Preventiva' },
  { value: 'corretiva', label: 'Corretiva' },
  { value: 'inspecao', label: 'Inspeção' },
  { value: 'limpeza', label: 'Limpeza / conservação' },
  { value: 'seguranca', label: 'Segurança' },
  { value: 'outros', label: 'Outros' },
];

function statusColor(status?: string): 'success' | 'warning' | 'default' {
  if (status === 'aberto') return 'warning';
  if (status === 'fechado') return 'success';
  return 'default';
}

function MaintenanceCard({ ticket, onClose }: { ticket: MachineMaintenanceTicket; onClose: (ticket: MachineMaintenanceTicket) => void }) {
  return (
    <Card elevation={2} sx={{ borderRadius: 3, border: '1px solid rgba(123,16,42,0.12)' }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1}>
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Typography variant="h6" fontFamily="Saira" fontWeight={900} color="primary.main">
                  Máquina {ticket.id}
                </Typography>
                <Chip size="small" color={statusColor(ticket.status)} label={ticket.status || 'sem status'} />
                <Chip size="small" variant="outlined" label={ticket.maintenance_type || 'tipo não informado'} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {summarizeMaintenanceTicket(ticket)}
              </Typography>
            </Box>
            <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
              <Typography variant="caption" color="text.secondary">Aberta em</Typography>
              <Typography variant="subtitle2" fontWeight={800}>{formatAuditTimestamp(ticket.stamp || ticket.created_at)}</Typography>
            </Stack>
          </Stack>

          <Typography variant="body1">{ticket.message || ticket.close_message || ticket.message_done || 'Sem mensagem registrada.'}</Typography>
          {ticket.close_message || ticket.message_done ? (
            <Alert severity="success">Fechamento: {ticket.close_message || ticket.message_done}</Alert>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Próxima manutenção: {ticket.next_maintenance_at || 'não definida'}
            </Typography>
            {ticket.status === 'aberto' ? (
              <Button variant="contained" color="primary" onClick={() => onClose(ticket)}>
                Fechar manutenção
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Fechada em {formatAuditTimestamp(ticket.stamp_end || ticket.closed_at)} por {ticket.closed_by || '—'}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function MaintenanceReports() {
  const { operation, equipament } = useContext(GlobalContext);
  const queryClient = useQueryClient();
  const [machineId, setMachineId] = useState(equipament || '');
  const [maintenanceType, setMaintenanceType] = useState('preventiva');
  const [message, setMessage] = useState('');
  const [nextMaintenanceAt, setNextMaintenanceAt] = useState('');
  const [closingTicket, setClosingTicket] = useState<MachineMaintenanceTicket | null>(null);
  const [closeMessage, setCloseMessage] = useState('');
  const [closeNextMaintenanceAt, setCloseNextMaintenanceAt] = useState('');
  const theme = useTheme();

  const dbReady = !!operation;
  const queryKey = ['machine-maintenance', operation];

  const maintenancesQuery = useQuery<MachineMaintenanceTicket[]>({
    queryKey,
    queryFn: () => listMachineMaintenances(String(operation || ''), {}),
    enabled: dbReady,
  });

  const machinesQuery = useQuery({
    queryKey: ['equipaments-list', operation],
    queryFn: () => listEquipaments(String(operation || '')),
    enabled: dbReady,
  });

  const machineOptions = useMemo(() => {
    return (machinesQuery.data || []).map((eq) => String(eq.id));
  }, [machinesQuery.data]);

  const openMutation = useMutation({
    mutationFn: () => openMachineMaintenance(String(operation || ''), {
      id: machineId.trim(),
      maintenance_type: maintenanceType,
      message: message.trim(),
      next_maintenance_at: nextMaintenanceAt.trim() || undefined,
    }),
    onSuccess: () => {
      setMessage('');
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => closeMachineMaintenance(String(operation || ''), {
      ticket_id: closingTicket?._id,
      id: closingTicket?.id,
      maintenance_type: maintenanceType,
      message: closeMessage.trim(),
      next_maintenance_at: closeNextMaintenanceAt.trim() || undefined,
    }),
    onSuccess: () => {
      setClosingTicket(null);
      setCloseMessage('');
      setCloseNextMaintenanceAt('');
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const tickets = maintenancesQuery.data || [];
  const openTickets = useMemo(() => tickets.filter((ticket) => ticket.status === 'aberto'), [tickets]);
  const closedTickets = useMemo(() => tickets.filter((ticket) => ticket.status !== 'aberto'), [tickets]);
  const formReady = dbReady && machineId.trim() && maintenanceType && message.trim();
  const closeReady = !!closingTicket && closeMessage.trim();

  return (
    <DrawerHeader title="Manutenção de Máquinas">
      <Stack spacing={3} sx={{ maxWidth: 1320, mx: 'auto' }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2.5, md: 3 },
            bgcolor: 'background.paper',
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                CONTROLE DE DISPONIBILIDADE
              </Typography>
              <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Gestão de Indisponibilidade e Reparos</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Registre e acompanhe as manutenções da frota em tempo real. Máquinas com ticket de manutenção em aberto são automaticamente removidas da contagem de disponibilidade no dashboard operacional.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`${openTickets.length} abertas`} color="primary" variant="outlined" />
              <Chip label={`${closedTickets.length} fechadas`} color="primary" variant="outlined" />
            </Stack>
          </Stack>
        </Paper>

        {!dbReady ? <Alert severity="warning">Selecione uma operação para gerenciar manutenções de máquinas.</Alert> : null}
        {openMutation.isError ? <Alert severity="error">Falha ao abrir manutenção. Verifique os campos e tente novamente.</Alert> : null}
        {closeMutation.isError ? <Alert severity="error">Falha ao fechar manutenção. Confirme se a manutenção ainda está aberta.</Alert> : null}

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" fontFamily="Saira" fontWeight={900}>Abrir manutenção</Typography>
                <Typography variant="body2" color="text.secondary">
                  Este registro é da máquina/frota monitorada, não do ticket de suporte do dispositivo de telemetria.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={machineOptions}
                    value={machineId || null}
                    onChange={(event, newValue) => setMachineId(newValue || '')}
                    loading={machinesQuery.isLoading}
                    renderInput={(params) => <TextField {...params} label="ID da máquina" />}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth select label="Tipo de manutenção" value={maintenanceType} onChange={(event) => setMaintenanceType(event.target.value)}>
                    {MAINTENANCE_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Próxima manutenção" placeholder="data ou horímetro" value={nextMaintenanceAt} onChange={(event) => setNextMaintenanceAt(event.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} label="O que será feito / motivo" value={message} onChange={(event) => setMessage(event.target.value)} />
                </Grid>
              </Grid>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" disabled={!formReady || openMutation.isPending} onClick={() => openMutation.mutate()}>
                  Abrir manutenção
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {closingTicket ? (
          <Card elevation={3} sx={{ borderRadius: 3, border: '1px solid rgba(165,16,51,0.32)' }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontFamily="Saira" fontWeight={900}>Fechar manutenção — Máquina {closingTicket.id}</Typography>
                <TextField fullWidth multiline minRows={3} label="Mensagem do que foi feito" value={closeMessage} onChange={(event) => setCloseMessage(event.target.value)} />
                <TextField fullWidth label="Atualizar próxima manutenção" placeholder="data ou horímetro" value={closeNextMaintenanceAt} onChange={(event) => setCloseNextMaintenanceAt(event.target.value)} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button variant="outlined" onClick={() => setClosingTicket(null)}>Cancelar</Button>
                  <Button variant="contained" disabled={!closeReady || closeMutation.isPending} onClick={() => closeMutation.mutate()}>Confirmar fechamento</Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Stack spacing={2}>
          <Typography variant="h5" fontFamily="Saira" fontWeight={900}>Manutenções abertas</Typography>
          {maintenancesQuery.isLoading ? <Alert severity="info">Carregando manutenções...</Alert> : null}
          {!maintenancesQuery.isLoading && openTickets.length === 0 ? <Alert severity="success">Nenhuma máquina em manutenção aberta.</Alert> : null}
          {openTickets.map((ticket) => <MaintenanceCard key={ticket._id || `${ticket.id}-${ticket.stamp}`} ticket={ticket} onClose={setClosingTicket} />)}
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Typography variant="h5" fontFamily="Saira" fontWeight={900}>Histórico recente</Typography>
          {closedTickets.slice(0, 25).map((ticket) => <MaintenanceCard key={ticket._id || `${ticket.id}-${ticket.stamp}`} ticket={ticket} onClose={setClosingTicket} />)}
        </Stack>
      </Stack>
    </DrawerHeader>
  );
}
